import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import {
  updateIdentityField,
  setIdentityType,
} from '../../Slice/unifiedFormSlice';
import * as S from './Styles';
import FileUpload from '../../helper/FileUpload';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeftOutlined';
import { Typography } from '@mui/material';
import Text from '../Texts';
import { personalFields } from './fields';
import NumberConverter from '../../helper/ConvertPersionNumberToEnglish';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import SubmitButton from '../Button/SubmitButton';

import { useNavigate, useLocation } from 'react-router-dom';
import uploadIcon from '/src/assets/images/identity/paper-upload.svg?url';
import { submitIdentityExtraInfo } from '../../thunks/submitIdentityExtraInfo';
// Was importing './validationLegal', which checks a company شناسه ملی against
// an individual's کد ملی. Both schemas were loose enough that it went unnoticed.
import { validationSchema } from './validationPersonal';

interface PersonalIdentityProps {
  onNext: () => void;
  identityType?: string;
}

const PersonalIdentity: React.FC<PersonalIdentityProps> = ({
  onNext,
  identityType,
}) => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const location = useLocation();
  const identity = useSelector(
    (state: RootState) => state.unifiedForm.identity,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [backendErrors, setBackendErrors] = useState<any>({});

  // استفاده از hook برای مدیریت submit
  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات هویت شخصی با موفقیت ثبت شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
    onSuccess: () => {
      // تاخیر قبل از navigate
      setTimeout(() => {
        onNext();
      }, 800);
    },
  });

  const { id, name, display_name, identity_type } = location.state || {};

  console.log('Location state:', { id, name, display_name, identity_type });

  useEffect(() => {
    if (identity_type) {
      dispatch(setIdentityType(identity_type));
    }
    if (id) {
      dispatch(updateIdentityField({ field: 'id', value: id }));
    }
    // These were crossed over: `name` was written into display_name and
    // vice versa, so resuming a draft showed each value in the wrong field.
    if (name) {
      dispatch(updateIdentityField({ field: 'name', value: name }));
    }
    if (display_name) {
      dispatch(
        updateIdentityField({ field: 'display_name', value: display_name }),
      );
    }
  }, [id, name, display_name, identity_type, dispatch]);

  const formik = useFormik({
    initialValues: {
      name: identity.name || '',
      display_name: identity.display_name || '',
      national_id: identity.national_id || '',
      address: identity.address || '',
      postal_code: identity.postal_code || '',
      phone: identity.phone || '',
      national_id_image: null, // افزودن برای ولیدیشن فایل
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        console.log('PersonalIdentity Form submitted:', {
          values,
          selectedFile,
          publisher_id: id || identity.id,
        });

        const response = await dispatch(
          submitIdentityExtraInfo({
            identity_type: identityType || 'PERSONAL',
            national_id: values.national_id,
            address: values.address,
            postal_code: values.postal_code,
            phone: values.phone,
            publisher_id: String(id || identity.id || ''),
            national_id_image: selectedFile,
          }),
        ).unwrap();

        Object.entries(values).forEach(([key, value]) => {
          dispatch(
            updateIdentityField({
              field: key as keyof typeof identity,
              value,
            }),
          );
        });

        return response;
      });
    },
  });

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    // See LegalIdentty: numeric fields are normalised on the way in so a code
    // typed in Persian digits or pasted with a space still validates.
    const isNumeric = personalFields.some(
      (field) => field.name === name && field.numeric,
    );
    const value = isNumeric
      ? NumberConverter.toNumericOnly(e.target.value)
      : e.target.value;

    formik.setFieldValue(name, value);
    dispatch(
      updateIdentityField({
        field: name as keyof typeof identity,
        value,
      }),
    );
    if (backendErrors[name]) {
      setBackendErrors({ ...backendErrors, [name]: '' });
    }
  };

  return (
    <S.Container>
      <S.Description variant="body1">
        {Text.PersonalIdentty.completedData}
      </S.Description>

      <form onSubmit={formik.handleSubmit}>
        <S.GridContainer>
          {personalFields.map((field) => (
            <S.FieldContainer key={field.name}>
              <S.Label>{field.label}</S.Label>
              <S.StyledTextField
                variant="outlined"
                placeholder={field.placeholder}
                size="small"
                name={field.name}
                value={
                  formik.values[field.name as keyof typeof formik.initialValues]
                }
                onChange={handleFieldChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched[
                    field.name as keyof typeof formik.initialValues
                  ] &&
                    !!formik.errors[
                      field.name as keyof typeof formik.initialValues
                    ]) ||
                  !!backendErrors[field.name]
                }
                helperText={
                  (formik.touched[
                    field.name as keyof typeof formik.initialValues
                  ] &&
                    formik.errors[
                      field.name as keyof typeof formik.initialValues
                    ]) ||
                  backendErrors[field.name]
                }
              />
            </S.FieldContainer>
          ))}
        </S.GridContainer>

        <S.FieldContainer>
          <S.Label>{Text.PersonalIdentty.ImageIdentity}</S.Label>
          <FileUpload
            onFilesSelected={(files) => {
              if (files && files.length > 0) {
                setSelectedFile(files[0]);
                formik.setFieldValue('national_id_image', files[0]);
              }
            }}
            imageSrc={uploadIcon}
            uploadText={Text.PersonalIdentty.uploadImage}
          />
          {formik.touched.national_id_image &&
            formik.errors.national_id_image && (
              <Typography color="error">
                {formik.errors.national_id_image}
              </Typography>
            )}
        </S.FieldContainer>

        {backendErrors.general && (
          <S.FieldContainer>
            <Typography color="error">{backendErrors.general}</Typography>
          </S.FieldContainer>
        )}

        <S.SubmitButtonBox>
          <SubmitButton
            type="submit"
            startIcon={<ArrowCircleLeft />}
            isSubmitting={isSubmitting}
            loadingText="در حال ارسال..."
            variant="contained"
            color="primary"
            fullWidth
          >
            {Text.PersonalIdentty.submite}
          </SubmitButton>
        </S.SubmitButtonBox>
      </form>
    </S.Container>
  );
};

export default PersonalIdentity;
